import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Collapse,
  Box,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useLlmConfigs, useProjectAuthorizers } from '@/providers';
import JOB_TYPES from '@/components/jobs/types';
import getLlmTypeLabel from '@/util/getLlmTypeLabel';
import { LLM_FAMILIES } from '@/components/jobs/JobForm/EndpointFields/EndpointLLM/llmFamilies';

const AUTHORIZER_TYPE_LABELS = {
  api_key: 'API Key',
  oidc: 'OIDC',
};

function AuthorizerReview({ authorizer }) {
  const { authorizers } = useProjectAuthorizers();
  if (!authorizer?.id) return null;

  const match = (authorizers || []).find((item) => item.id === authorizer.id);
  const name = match?.name || authorizer.id;
  const type_label =
    AUTHORIZER_TYPE_LABELS[match?.type] || match?.type || '';

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='h6' sx={{ mb: 1 }}>
        Authorizer
      </Typography>
      <Typography>
        Name: <b>{name}</b>
      </Typography>
      {type_label ? (
        <Typography>
          Type: <b>{type_label}</b>
        </Typography>
      ) : null}
    </Box>
  );
}

function GuardrailServiceReview({ title, service, project_services }) {
  if (!service?.id) return null;

  const matched = project_services.find(
    (s) => (s.id || s.service_id) === service.id,
  );
  const service_label = matched
    ? `${matched.name} - ${service.id}`
    : service.id;

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant='body1' fontWeight='medium'>
        {title}
      </Typography>
      <Typography>
        Service: <b>{service_label}</b>
      </Typography>
      {service.api_key ? (
        <Typography>
          API Key: <b>••••••••</b>
        </Typography>
      ) : null}
      {service.model ? (
        <Typography>
          Model: <b>{service.model}</b>
        </Typography>
      ) : null}
    </Box>
  );
}

function GuardrailReview({ guardrail, project_services }) {
  if (!guardrail) return null;

  return (
    <>
      <Typography variant='h6' gutterBottom>
        Guardrail
      </Typography>
      <GuardrailServiceReview
        title='Protected Service'
        service={guardrail.protected_service}
        project_services={project_services}
      />
      <Typography>
        Content Safety:{' '}
        <b>{guardrail.content_safety ? 'Enabled' : 'Disabled'}</b>
      </Typography>
      {guardrail.content_safety ? (
        <GuardrailServiceReview
          title='Content Safety Service'
          service={guardrail.content_safety_service}
          project_services={project_services}
        />
      ) : null}
      <Typography>
        Jailbreak Detection:{' '}
        <b>{guardrail.jailbreak_detection ? 'Enabled' : 'Disabled'}</b>
      </Typography>
      {guardrail.jailbreak_detection &&
      guardrail.jailbreak_detection_service?.id ? (
        <GuardrailServiceReview
          title='Jailbreak Detection Service'
          service={guardrail.jailbreak_detection_service}
          project_services={project_services}
        />
      ) : null}
      <Typography>
        Injection Detection:{' '}
        <b>{guardrail.injection_detection ? 'Enabled' : 'Disabled'}</b>
      </Typography>
    </>
  );
}

export default function EndpointReview({ type, data }) {
  const [expanded, setExpanded] = useState(true);
  const project_services = useSelector(
    (state) => state.projectServices || [],
  );
  const llm_configs = useLlmConfigs() || {};

  if (!data) {
    return null;
  }

  const { routes, start_command, service_id, services, llm, authorizer, guardrail } =
    data;
  const type_label = getLlmTypeLabel(llm?.type) || llm?.type;
  const family_label =
    llm?.type && llm?.family && llm_configs[llm.type]?.[llm.family]?.label
      ? llm_configs[llm.type][llm.family].label
      : llm?.family;
  const is_custom_family = llm?.family === LLM_FAMILIES.CUSTOM;

  // Support both legacy service_id and new services array format
  const services_list = services || (service_id ? [{ id: service_id }] : []);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          p: 1,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size='small'>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        <Typography variant='h6'>Endpoint</Typography>
      </Box>
      <Collapse in={expanded}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              {type === JOB_TYPES.LLM.value ? (
                <>
                  <Typography variant='h6' gutterBottom>
                    LLM
                  </Typography>
                  <Typography>
                    Type: <b>{type_label}</b>
                  </Typography>
                  <Typography>
                    Family: <b>{family_label}</b>
                  </Typography>
                  {!is_custom_family ? (
                    <Typography>
                      Size: <b>{llm?.size}</b>
                    </Typography>
                  ) : null}
                  {llm?.reasoning_parser ? (
                    <Typography>
                      Reasoning Parser Override: <b>{llm.reasoning_parser}</b>
                    </Typography>
                  ) : null}
                  {llm?.tool_parser ? (
                    <Typography>
                      Tool Parser Override: <b>{llm.tool_parser}</b>
                    </Typography>
                  ) : null}
                  {llm?.additional_kwargs ? (
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      Additional Args Override: <b>{llm.additional_kwargs}</b>
                    </Typography>
                  ) : null}
                  {llm?.name ? (
                    <Typography>
                      {is_custom_family ? 'Model Name' : 'Model Name Override'}:{' '}
                      <b>{llm.name}</b>
                    </Typography>
                  ) : null}
                  {is_custom_family && llm?.moe ? (
                    <Typography>
                      MoE Model: <b>Yes</b>
                    </Typography>
                  ) : null}
                  {is_custom_family && llm?.quantization ? (
                    <Typography>
                      Quantization: <b>{llm.quantization}</b>
                    </Typography>
                  ) : null}
                  {llm?.concurrent_requests ? (
                    <Typography>
                      Concurrent Requests: <b>{llm.concurrent_requests}</b>
                    </Typography>
                  ) : null}
                  <AuthorizerReview authorizer={authorizer} />
                </>
              ) : type === JOB_TYPES.GUARDRAIL.value ? (
                <>
                  <GuardrailReview
                    guardrail={guardrail}
                    project_services={project_services}
                  />
                  <AuthorizerReview authorizer={authorizer} />
                </>
              ) : routes && routes.length ? (
                <>
                  <Typography variant='h6' gutterBottom>
                    Routes
                  </Typography>
                  {routes.map((route) => (
                    <Paper
                      key={`${route.verb}_${route.path}`}
                      sx={{ p: 2, mb: 2 }}
                      variant='outlined'
                    >
                      <Typography variant='h6' gutterBottom>
                        Route: {route.path} : {route.verb}
                      </Typography>
                      <Typography>
                        <b>{route.verb}</b> to <b>{route.path}</b> calls function{' '}
                        <b>{route.function}</b> in file <b>{route.file}</b> using{' '}
                        <b>{route.positional ? 'positional' : 'keyword'}</b>{' '}
                        arguments
                      </Typography>
                      {['POST', 'PUT', 'PATCH'].includes(route.verb) &&
                      route.body &&
                      route.body.length ? (
                        <>
                          <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
                            Required Request Body
                          </Typography>
                          <TableContainer>
                            <Table size='small'>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Optional</TableCell>
                                  <TableCell>Default Value</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {route.body.map((param, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      <b>{param.name}</b>
                                    </TableCell>
                                    <TableCell>{param.type}</TableCell>
                                    <TableCell>
                                      <Checkbox checked={param.optional} disabled />
                                    </TableCell>
                                    <TableCell>{param.default_value}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      ) : null}
                    </Paper>
                  ))}
                </>
              ) : (
                <>
                  <Typography>
                    Start server with command: <b>{start_command}</b>
                  </Typography>
                  <AuthorizerReview authorizer={authorizer} />
                </>
              )}
              {services_list && services_list.length ? (
                <>
                  <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
                    Regional Services
                  </Typography>
                  {services_list.map((service_item, idx) => {
                    const service_id_to_find =
                      service_item.id || service_item.service_id;
                    const service = project_services.find(
                      (s) => (s.id || s.service_id) === service_id_to_find,
                    );
                    if (service) {
                      return (
                        <Typography key={idx} sx={{ mb: 1 }}>
                          Attach to regional service <b>{service.name}</b>
                          {service_item.target_port ? (
                            <>
                              {' '}
                              on port <b>{service_item.target_port}</b>
                            </>
                          ) : service.resource ? (
                            <>
                              {' '}
                              on port <b>{service.resource}</b>
                            </>
                          ) : null}
                          {service.hostname ? (
                            <>
                              {' '}
                              at hostname <b>{service.hostname}</b>
                            </>
                          ) : null}
                        </Typography>
                      );
                    }
                    return (
                      <Typography key={idx} sx={{ mb: 1 }}>
                        Attach to regional service (ID: {service_id_to_find})
                      </Typography>
                    );
                  })}
                </>
              ) : null}
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  );
}

